/**
 * Readonly превью WABA кнопок из Meta/Wazzup
 * Дата: 07.02.2026 - Создан для TODO-053 (Template Buttons ↔ Auto-Replies)
 * 
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - frontend/src/pages/Templates.tsx - используется для отображения кнопок WABA шаблонов
 * - frontend/src/api/templates.ts - WABATemplate, WABAButton интерфейсы
 * 
 * КАК РАБОТАЕТ:
 * 1. Отображает кнопки WABA шаблона в readonly режиме
 * 2. Показывает тип кнопки: QUICK_REPLY, URL, PHONE_NUMBER
 * 3. Используется только для WABA шаблонов (template_type = 'waba_template')
 * 
 * СХЕМА:
 * Templates.tsx → WABAButtonsPreview → отображение кнопок из selectedWabaTemplate.buttons
 */
import { Badge } from '@/components/ui';
import { WABAButton } from '@/api/templates';

interface WABAButtonsPreviewProps {
  buttons: WABAButton[];
  testId?: string;
}

export function WABAButtonsPreview({ buttons, testId }: WABAButtonsPreviewProps) {
  if (!buttons || buttons.length === 0) {
    return (
      <div className="text-sm text-neutral-500 p-3 border border-dashed rounded-lg text-center">
        Нет кнопок в этом WABA шаблоне
      </div>
    );
  }

  const getButtonTypeLabel = (type: string): string => {
    switch (type) {
      case 'QUICK_REPLY':
        return 'Quick Reply';
      case 'URL':
        return 'URL';
      case 'PHONE_NUMBER':
        return 'Phone Number';
      default:
        return type;
    }
  };

  const getButtonTypeColor = (type: string): string => {
    switch (type) {
      case 'QUICK_REPLY':
        return 'bg-blue-100 text-blue-700';
      case 'URL':
        return 'bg-green-100 text-green-700';
      case 'PHONE_NUMBER':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-neutral-100 text-neutral-700';
    }
  };

  return (
    <div className="space-y-2" data-testid={testId || 'waba-buttons-preview'}>
      <div className="text-sm font-medium text-neutral-700 mb-2">
        🏷️ Кнопки из Meta (READ-ONLY)
      </div>
      <div className="flex flex-wrap gap-2">
        {buttons.map((button, index) => (
          <div
            key={index}
            className="flex items-center gap-2 p-2 border rounded-lg bg-white"
            data-testid={`waba-button-${index}`}
          >
            <span className="text-sm font-medium">{button.text}</span>
            <Badge className={getButtonTypeColor(button.type)}>
              {getButtonTypeLabel(button.type)}
            </Badge>
            {button.type === 'URL' && button.url && (
              <span className="text-xs text-neutral-500 truncate max-w-xs" title={button.url}>
                {button.url}
              </span>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-neutral-500 mt-2">
        ⚠️ Кнопки настраиваются в ЛК Wazzup/Meta Business. Здесь они отображаются только для просмотра.
      </p>
    </div>
  );
}
